import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { AccountSettingForm } from "~/components/specifics/vendors/settings/accounts/AccountSettingForm";
import type { VendorCompanyUpdatePasswordParam } from "~/models/VendorCompany";
import {
	commonNewPasswordValidationSchema,
	requireOldPasswordValidationSchema
} from "~/models/VendorCompany";
import type { VendorSession } from "~/models/VendorSession";
import { isSuccess } from "~/utils/api/api_handler";
import { changePassword } from "~/utils/api/vendors/VendorCompany";

type Props = {
	session: VendorSession;
};

export function AccountSetting({ session }: Props) {
	const isAdmin = session.admin;

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, isSubmitting },
		reset
	} = useForm<VendorCompanyUpdatePasswordParam>({
		defaultValues: {
			confirmPassword: "",
			newPassword: "",
			oldPassword: ""
		},
		mode: "onBlur",
		resolver: yupResolver(
			isAdmin
				? commonNewPasswordValidationSchema
				: requireOldPasswordValidationSchema
		)
	});

	const onSubmit = async (
		passwords: VendorCompanyUpdatePasswordParam
	): Promise<void> => {
		const result = await changePassword(passwords);
		if (isSuccess(result)) {
			reset();
			toast.success("パスワードを変更しました。");
		} else {
			toast.error(result.message ?? "エラーが発生しました。");
		}
	};
	return (
		<Panel>
			<PanelHeader title="アカウント詳細" />
			<PanelBody>
				<AccountSettingForm session={session} />
				<Heading as="h3" mb={designTokens["spacing/m"]}>
					パスワードの変更
				</Heading>
				<form noValidate onSubmit={handleSubmit(onSubmit)}>
					<FormGroup maxW="32rem" variant="vertical">
						<FormControl
							chip={isAdmin ? undefined : "必須"}
							isInvalid={!!errors.oldPassword}
							label="現在のパスワード"
						>
							<Input
								{...register("oldPassword")}
								autoComplete="current-password"
								disabled={isAdmin}
								type="password"
							/>
							<FormControlText error={errors.oldPassword?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.newPassword}
							label="新しいパスワード"
						>
							<Input
								{...register("newPassword")}
								autoComplete="new-password"
								placeholder="例）8文字以上のパスワード"
								type="password"
							/>
							<FormControlText error={errors.newPassword?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.confirmPassword}
							label="新しいパスワード（確認用）"
						>
							<Input
								{...register("confirmPassword")}
								autoComplete="new-password"
								type="password"
							/>
							<FormControlText error={errors.confirmPassword?.message} />
						</FormControl>
					</FormGroup>
					<Button
						disabled={!isDirty}
						loading={isSubmitting}
						mt={designTokens["spacing/m"]}
						type="submit"
						variant="primary"
					>
						パスワードを変更
					</Button>
				</form>
			</PanelBody>
		</Panel>
	);
}
